import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Book, CoverSettings } from '../../types';
import { Button } from '../common';
import CoverImageSection from '../books/CoverImageSection';

interface CoverStepProps {
  book: Book;
  onBookChange: (book: Book) => void;
  onNext: () => void;
  onBack: () => void;
}

export default function CoverStep({ book, onBookChange, onNext, onBack }: CoverStepProps) {
  const { t } = useTranslation();
  const topRef = useRef<HTMLDivElement>(null);
  const [coverImageUrl, setCoverImageUrl] = useState<string | null>(book.coverImageUrl || null);
  const [coverImageOriginalUrl, setCoverImageOriginalUrl] = useState<string | null>(
    book.coverImageOriginalUrl || null
  );
  const [, setCoverSettings] = useState<CoverSettings | null>(book.coverSettings || null);

  // Auto scroll to top when component mounts
  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // Sync state with book prop when it changes
  useEffect(() => {
    setCoverImageUrl(book.coverImageUrl || null);
    setCoverImageOriginalUrl(book.coverImageOriginalUrl || null);
    setCoverSettings(book.coverSettings || null);
  }, [book.coverImageUrl, book.coverImageOriginalUrl, book.coverSettings]);

  const handleCoverChange = (
    url: string | null,
    originalUrl?: string | null,
    settings?: CoverSettings | null
  ) => {
    setCoverImageUrl(url);
    if (originalUrl !== undefined) {
      setCoverImageOriginalUrl(originalUrl);
    }
    if (settings !== undefined) {
      setCoverSettings(settings);
    }

    // Update the book object to reflect cover changes
    onBookChange({
      ...book,
      coverImageUrl: url || undefined,
      coverImageOriginalUrl:
        originalUrl !== undefined ? originalUrl || undefined : book.coverImageOriginalUrl,
      coverSettings: settings !== undefined ? settings || undefined : book.coverSettings,
    });
  };

  const handleNext = () => {
    onNext();
  };

  return (
    <motion.div
      ref={topRef}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          {t('wizard.coverStep')}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 mt-1">{t('wizard.coverStepSubtitle')}</p>
      </div>

      {/* Cover Image Section */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
        <CoverImageSection
          bookId={book.id}
          coverImageUrl={coverImageUrl}
          coverImageOriginalUrl={coverImageOriginalUrl}
          onCoverChange={handleCoverChange}
        />
      </div>

      {/* Navigation */}
      <div className="flex gap-4 pt-4">
        <Button variant="secondary" onClick={onBack}>
          {t('wizard.back')}
        </Button>
        <Button className="flex-1" onClick={handleNext}>
          {coverImageUrl ? t('wizard.finish') : t('common.skip')}
        </Button>
      </div>
    </motion.div>
  );
}
